/**
 * Audio playback hook for playing audio from base64 data
 *
 * Used to play audio responses from gpt-audio-mini
 */
import { useState, useRef, useCallback } from 'react';

interface UseAudioPlaybackReturn {
  isPlaying: boolean;
  play: (base64Audio: string) => void;
  stop: () => void;
  error: string | null;
}

export function useAudioPlayback(): UseAudioPlaybackReturn {
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const play = useCallback((base64Audio: string) => {
    try {
      setError(null);

      // Stop any currently playing audio
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }

      // Convert base64 to blob - detect format from header or default to mpeg
      const byteCharacters = atob(base64Audio);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);

      // Detect audio format from magic bytes
      let mimeType = 'audio/mpeg'; // Default to MP3
      if (byteArray.length > 4) {
        // WAV: starts with "RIFF"
        if (byteArray[0] === 0x52 && byteArray[1] === 0x49 && byteArray[2] === 0x46 && byteArray[3] === 0x46) {
          mimeType = 'audio/wav';
        }
      }

      const blob = new Blob([byteArray], { type: mimeType });

      // Create audio element and play
      const audio = new Audio(URL.createObjectURL(blob));
      audioRef.current = audio;

      audio.onplay = () => setIsPlaying(true);
      audio.onended = () => {
        setIsPlaying(false);
        URL.revokeObjectURL(audio.src);
      };
      audio.onerror = () => {
        setError('Failed to play audio');
        setIsPlaying(false);
      };

      audio.play().catch((err) => {
        console.error('Audio playback error:', err);
        setError('Failed to play audio');
        setIsPlaying(false);
      });
    } catch (err) {
      console.error('Error playing audio:', err);
      setError('Failed to play audio');
    }
  }, []);

  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  }, []);

  return {
    isPlaying,
    play,
    stop,
    error,
  };
}
